import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Select } from '@atlas/ui';

import Field from 'common/Form/Field';
import { getIsFieldRequired, isFunction } from 'common/utils';

const SelectField = ({ field, onBlur, onChange, onFocus }) => {
  const {
    active,
    autocomplete,
    autofocus,
    disabled,
    errors,
    isModifiable = true,
    label,
    name,
    options: initialOptions,
    rules,
    touched,
    translateOptions = true,
    value,
    warnings,
  } = field;
  const initialValue = useRef(value);
  const areFieldsAsync = useMemo(() => isFunction(initialOptions), [initialOptions]);
  const [options, setOptions] = useState(areFieldsAsync ? [] : initialOptions);

  useEffect(() => {
    if (areFieldsAsync) {
      (async () => {
        const loadedOptions = await initialOptions();

        setOptions(loadedOptions);
      })();
    }
  }, [areFieldsAsync, initialOptions]);

  useEffect(() => {
    if (!areFieldsAsync && initialOptions !== options) {
      setOptions(initialOptions);
    }
  }, [areFieldsAsync, initialOptions, options]);

  const error = touched && errors[0];
  const isRequiredSymbol = getIsFieldRequired(rules);

  const changeHandler = useCallback(
    val => {
      if (initialValue.current === '<not-selected>' && val === '<not-selected>') {
        onChange(null);
      } else {
        onChange(
          val,
          true, // This parameter is required to handle touched state for select
        );
      }
    },
    [onChange],
  );

  useEffect(() => {
    if (initialValue.current === '<not-selected>' && value === '<not-selected>') {
      changeHandler(null);
    }
  }, [changeHandler, value]);

  const sortedOptions = useMemo(
    () => options.sort((a, b) => a.text.localeCompare(b.text)),
    [options],
  );

  const shouldHaveNullValue = !!sortedOptions.find(option => option.value === '<not-selected>');

  const displayOptions = useMemo(() => {
    const selectedOption = sortedOptions.find(o => o.value === value);

    return selectedOption
      ? [
          ...(initialValue.current === '<not-selected>'
            ? [
                {
                  attributes: [],
                  disabled: true,
                  isActive: true,
                  text: 'not_selected',
                  value: '<not-selected>',
                },
              ]
            : []),
          selectedOption,
          ...sortedOptions.filter(o => o.value !== value),
        ]
      : [
          ...(initialValue.current === '<not-selected>'
            ? [
                {
                  attributes: [],
                  disabled: true,
                  isActive: true,
                  text: 'not_selected',
                  value: '<not-selected>',
                },
              ]
            : []),
          ...sortedOptions,
        ];
  }, [sortedOptions, value]);

  return (
    <Field options={field}>
      <Select
        active={active}
        autoComplete={autocomplete}
        autofocus={autofocus}
        disabled={disabled || !isModifiable}
        error={error}
        label={label}
        name={name}
        onBlur={onBlur}
        onChange={changeHandler}
        onFocus={onFocus}
        options={displayOptions}
        placeholder={!isRequiredSymbol}
        touched={touched}
        translateOptions={translateOptions}
        value={shouldHaveNullValue && value === null ? '<not-selected>' : value}
        warnings={warnings}
      />
    </Field>
  );
};

SelectField.propTypes = {
  field: PropTypes.shape({
    active: PropTypes.bool,
    autocomplete: PropTypes.bool,
    autofocus: PropTypes.bool,
    disabled: PropTypes.bool,
    errors: PropTypes.arrayOf(PropTypes.object),
    isModifiable: PropTypes.bool,
    label: PropTypes.string,
    name: PropTypes.string,
    options: PropTypes.array,
    rules: PropTypes.array,
    touched: PropTypes.bool,
    translateOptions: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    warnings: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  }).isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
};

export default memo(SelectField);
